import React, { PropsWithChildren, useState } from 'react'
import { createContext } from 'lib/createContext'
import { useRequirementDetailContextValue } from './RequirementDetailProvider'

type Workflow<Args extends Record<string, unknown> | undefined = undefined> = {
  open: Args extends undefined ? () => void : (args: Args) => void
  close: () => void
  isOpen: boolean
} & (Args extends undefined ? object : { data?: Args })

type QuestionFlowArgs = {
  question: ReturnType<
    typeof useRequirementDetailContextValue
  >['state']['questions'][number]
}

export type RequirementDetailWorkflowsContextState = {
  editQuestionFlow: Workflow<QuestionFlowArgs>
  removeQuestionFlow: Workflow<QuestionFlowArgs>
  restoreQuestionFlow: Workflow<QuestionFlowArgs>
}

type WorkflowState<T extends Record<string, unknown> | undefined = undefined> =
  T extends undefined
    ? boolean
    : {
        isOpen: boolean
        data?: T
      }

const [
  RequirementDetailWorkflowsContext,
  useRequirementDetailWorkflowsContextValue,
] = createContext<RequirementDetailWorkflowsContextState>({
  name: 'RequirementDetailWorkflowsContext',
})

export const RequirementDetailWorkflowsProvider = ({
  children,
}: PropsWithChildren) => {
  const [editQuestionFlow, setEditQuestionFlow] = useState<
    WorkflowState<QuestionFlowArgs>
  >({ isOpen: false, data: undefined })

  const [removeQuestionFlow, setRemoveQuestionFlow] = useState<
    WorkflowState<QuestionFlowArgs>
  >({ isOpen: false, data: undefined })

  const [restoreQuestionFlow, setRestoreQuestionFlow] = useState<
    WorkflowState<QuestionFlowArgs>
  >({ isOpen: false, data: undefined })

  const value: RequirementDetailWorkflowsContextState = {
    editQuestionFlow: {
      open: (data) => setEditQuestionFlow({ isOpen: true, data }),
      close: () => setEditQuestionFlow({ isOpen: false, data: undefined }),
      isOpen: editQuestionFlow.isOpen,
      data: editQuestionFlow.data,
    },
    removeQuestionFlow: {
      open: (data) => setRemoveQuestionFlow({ isOpen: true, data }),
      close: () => setRemoveQuestionFlow({ isOpen: false, data: undefined }),
      isOpen: removeQuestionFlow.isOpen,
      data: removeQuestionFlow.data,
    },
    restoreQuestionFlow: {
      open: (data) => setRestoreQuestionFlow({ isOpen: true, data }),
      close: () => setRestoreQuestionFlow({ isOpen: false, data: undefined }),
      isOpen: restoreQuestionFlow.isOpen,
      data: restoreQuestionFlow.data,
    },
  }

  return (
    <RequirementDetailWorkflowsContext.Provider value={value}>
      {children}
    </RequirementDetailWorkflowsContext.Provider>
  )
}

export { useRequirementDetailWorkflowsContextValue }
